import _isUndefined from 'lodash/isUndefined';
import _omitBy from 'lodash/omitBy';
import _pick from 'lodash/pick';
import { eventChannel } from 'redux-saga';

import { VHLiveResponse } from '../../../../common/types/broadcast';
import { UgcLiveStatus } from '../../pages/call/types';
import { EventEmitter } from './event-emitter';
import { createLogger } from './loggerFacade';
import { api } from './transport/api';

export interface VHLiveEventData {
    viewers?: number;
    ugc_live_status?: UgcLiveStatus;
    start_time?: number;
}

export interface SubscribeOptions {
    streamUrl: string;
    pollUntilOnAir?: boolean;
}

const UGC_LIVE_STATUS_UPDATE_INTERVAL = 2000;
const VIEWERS_COUNT_UPDATE_INTERVAL = 15000;
const logger = createLogger('vh-live-events');

export const fetchStreamInfo = async (streamUrl: string) => {
    const streamInfo = await api<VHLiveResponse>(streamUrl);
    return _omitBy(_pick(streamInfo.content, ['viewers', 'ugc_live_status', 'start_time']), _isUndefined);
};

class VHLiveEventsEmitter extends EventEmitter {
    private readonly options: SubscribeOptions;

    private pollTimer?: ReturnType<typeof setInterval>;
    private isDestroyed = false;
    private fetchInProcess = false;
    constructor(options: SubscribeOptions) {
        super();

        this.options = options;
        this.fetchStreamInfo = this.fetchStreamInfo.bind(this);
    }

    async fetchStreamInfo() {
        try {
            if (this.fetchInProcess) {
                return;
            }
            this.fetchInProcess = true;
            const vhLiveData = await fetchStreamInfo(this.options.streamUrl);
            if (this.isDestroyed) {
                return;
            }
            this.emit('data', vhLiveData);
            return vhLiveData;
        } catch (error) {
            logger.error(error, {
                method: 'VHLiveEventsEmitter.fetchStreamInfo',
            });
            throw error;
        } finally {
            this.fetchInProcess = false;
        }
    }

    async start() {
        this.isDestroyed = false;
        await this.fetchStreamInfo();
        if (this.isDestroyed) {
            return;
        }
        this.pollTimer = setInterval(
            async () => {
                const vhLiveData = await this.fetchStreamInfo();
                if (this.isDestroyed || !vhLiveData) {
                    return;
                }
                if (this.options.pollUntilOnAir && vhLiveData.ugc_live_status === 'onair' && this.pollTimer) {
                    clearInterval(this.pollTimer);
                    this.pollTimer = setInterval(this.fetchStreamInfo, VIEWERS_COUNT_UPDATE_INTERVAL);
                }
            },
            this.options.pollUntilOnAir ? UGC_LIVE_STATUS_UPDATE_INTERVAL : VIEWERS_COUNT_UPDATE_INTERVAL
        );
    }

    destroy() {
        this.isDestroyed = true;
        if (this.pollTimer) {
            clearInterval(this.pollTimer);
        }
    }
}

export const createVHLiveEventsChannel = (options: SubscribeOptions) => {
    const emitter = new VHLiveEventsEmitter(options);
    const channel = eventChannel<VHLiveEventData>((emit) => {
        emitter.on('data', (data) => {
            emit(data);
        });

        return () => {
            emitter.destroy();
        };
    });

    emitter.start();

    return channel;
};
